import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { PushPrePermissionModal } from './PushPrePermissionModal';
import {
    getPushSupport,
    hasPushRewardPending,
    isPushModalVisible,
    isPushPromotionTarget,
    isPushSnoozed,
    isStandalone,
    logPushFunnelEvent,
    logPwaInstallOnce,
    setPushSnooze,
    syncExistingSubscription,
} from '../../lib/webPush';

/**
 * 푸시 허용 유도 노출 결정자 — App에 1회 마운트되어 전 라우트에서 작동한다.
 *
 * 노출 규칙:
 * - 로그인 유저만 (유저 단위 추적이 목적이므로 익명에게는 띄우지 않는다)
 * - /admin, /auth 경로 제외
 * - 리퍼럴 화면(/referral-guide 홍보 따라 하기, /referral-dashboard 참여·추천 현황) 제외 — 2026-10-08 대표 "해결해":
 *   카카오 친구톡 [올릴 글 받기]로 글을 복사하러 온 손님에게 2.5초 뒤 이 창이 따라 하기 화면을 덮었다.
 *   카카오톡 안 화면은 알림을 켤 수 없어 '네'를 누르면 '기본 브라우저에서 계속' 안내로 흐름 밖으로 나간다.
 * - 이미 허용(granted)이면 조용히 서버 구독만 재동기화하고 안 띄움
 * - 차단(denied)이면 안 띄움 (복구는 설정 화면에서)
 * - '나중에' 선택 시 7일 쿨다운 — 단, 아래 골든 스팟은 쿨다운을 무시한다:
 *   · 알림톡 딥링크 착지(?from=alimtalk): 방금 알림의 가치를 체험한 순간
 *   · 홈 화면 설치 직후 첫 실행(standalone 최초 감지): 설치라는 큰 행동 직후
 * - ?push_demo=1: 강제 노출 (데모, 퍼널 이벤트는 source='demo'로 분리 집계)
 *
 * iOS 설치 완주 루프: 모달이 설치 안내를 보여주고 → 유저가 홈 화면에서 앱을 열면
 * standalone 첫 실행이 감지되어 pwa_installed 기록 + 모달 재노출 → 진짜 권한 요청.
 */

const EXCLUDED_PREFIXES = ['/admin', '/auth', '/referral-guide', '/referral-dashboard'];

export const PushPermissionGate: React.FC = () => {
    const location = useLocation();
    const user = useAuthStore((state) => state.user);
    const [open, setOpen] = useState(false);
    const [source, setSource] = useState('organic');
    const decidedRef = useRef(false);

    useEffect(() => {
        // 안드로이드 설치 이벤트 (iOS에는 없음 — standalone 첫 실행으로 감지)
        const onInstalled = () => {
            logPushFunnelEvent('pwa_installed', { source: 'android-event' });
        };
        window.addEventListener('appinstalled', onInstalled);
        return () => window.removeEventListener('appinstalled', onInstalled);
    }, []);

    useEffect(() => {
        if (decidedRef.current || open) return;

        let cancelled = false;
        let timer: number | undefined;

        const params = new URLSearchParams(window.location.search);
        const isDemo = params.has('push_demo');
        const fromAlimtalk = params.get('from') === 'alimtalk';

        const run = async () => {
            if (isDemo) {
                decidedRef.current = true;
                setSource('demo');
                setOpen(true);
                return;
            }
            if (!user) return;

            // 홈 화면 설치 첫 실행 감지 (기록은 로그인 후 1회)
            const freshInstall = await logPwaInstallOnce();
            if (cancelled) return;

            if (EXCLUDED_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) return;

            const support = getPushSupport();
            if (support === 'unsupported') return;

            if (support === 'ready') {
                if (Notification.permission === 'denied') return;
                if (Notification.permission === 'granted') {
                    // 이미 허용된 유저 — 팝업 없이 서버 구독만 재동기화
                    syncExistingSubscription();
                    return;
                }
            }

            // 유도는 모바일 전용 — 데스크톱은 자동 모달을 띄우지 않는다
            if (!isPushPromotionTarget()) return;

            const goldenSpot = fromAlimtalk || freshInstall;
            if (!goldenSpot && isPushSnoozed()) return;

            decidedRef.current = true;
            // 리워드 여정 중(카드 CTA 클릭 후 설치 왕복 등)이면 출처를 캠페인으로 이어받는다
            // — iOS 캠페인 구독이 'standalone'으로 새서 측정이 과소집계되는 것 방지
            setSource(hasPushRewardPending() ? 'credit_reward'
                : fromAlimtalk ? 'alimtalk' : freshInstall ? 'standalone' : 'organic');
            timer = window.setTimeout(() => {
                // 유저가 그 사이 벨/배너로 이미 모달을 열었다면 겹쳐 띄우지 않는다
                if (!cancelled && !isPushModalVisible()) setOpen(true);
            }, fromAlimtalk || freshInstall ? 1200 : 2500);
        };

        run();
        return () => {
            cancelled = true;
            if (timer) window.clearTimeout(timer);
        };
    }, [user, location.pathname, open]);

    const handleClose = useCallback(() => {
        setOpen(false);
        if (source !== 'demo') {
            setPushSnooze();
        }
    }, [source]);

    return <PushPrePermissionModal isOpen={open} onClose={handleClose} source={source} />;
};

export default PushPermissionGate;
